import { useEffect, useState } from "react";
import { Button, Card, CardHeader, CardContent, Box, Grid, Divider } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridCallbackDetails, GridColDef, GridFilterModel } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { FormInput } from "../../components/FormInput";
import { FormAsyncAutocomplete } from "../../components/FormAsyncAutoComplete";
import { ConfirmationDialog } from "../../components/ConfirmationDialog";
import { CustomTableToolbar } from "../../components/CustomTableToolbar";
import useNotifications from "../../components/NotificationProvider";
import OrganizationService from "../../services/organization.service";
import AccountService from "../../services/account.service";
import { IRowFilter, ITableRowsState } from "../../types/app/common.type";
import { IOrganization } from "../../types/api/organization.type";

const Organization = () => {
  const { t } = useTranslation("common");
  const {
    control,
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm({});
  const [openOrganizationDialog, setOpenOrganizationDialog] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [rowsState, setRowsState] = useState<ITableRowsState>({
    page: 0,
    pageSize: 5,
  });
  const [totalRows, setTotalRows] = useState<number>(0);
  const [rowFilter, setRowFilter] = useState<IRowFilter>({});
  const [tableRows, setTableRows] = useState<IOrganization[]>([]);
  const [selectedRow, setSelectedRow] = useState<IOrganization[]>([]);
  const [searchText, setSearchText] = useState("");
  const [showDeleted, setShowDeleted] = useState(false);
  const [filterButtonEl, setFilterButtonEl] = useState<HTMLButtonElement | null>(null);
  const { setNotification } = useNotifications();

  const columns: GridColDef[] = [
    { field: "org_id", headerName: "Organization ID", flex: 1 },
    { field: "org_name", headerName: "Name", flex: 1 },
    { field: "account_id", headerName: "Account ID", flex: 1 },
    { field: "parent_org", headerName: "Parent Organization", flex: 1 },
    { field: "status", headerName: "Status", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        if (params.row.deleted) {
          return <></>;
        }
        return (
          <>
            <EditIcon
              sx={{ cursor: "pointer", marginRight: "10px" }}
              onClick={() => {
                console.log("edit---", params);
                setOpenOrganizationDialog(true);
                reset({ ...params.row });
              }}
            />
            <DeleteIcon
              sx={{ cursor: "pointer", marginRight: "10px" }}
              onClick={() => {
                console.log("delete---", params);
                setSelectedRow([{ ...params.row }]);
                setOpenDeleteConfirmation(true);
              }}
            />
          </>
        );
      },
    },
  ];

  useEffect(() => {
    getOrganizations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsState, rowFilter, showDeleted]);

  useEffect(() => {
    if (searchText) {
      const delayDebounceFn = setTimeout(() => {
        setRowFilter({ org_name: searchText });
      }, 1000);
      return () => clearTimeout(delayDebounceFn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const getOrganizations = async () => {
    try {
      const result = await OrganizationService.getOrganizations({
        count_per_page: rowsState.pageSize,
        page_no: rowsState.page + 1,
        filter: rowFilter,
        show_deleted: showDeleted,
      });
      setTotalRows(result.data?.total);
      setTableRows(result.data?.results);
    } catch (e) {
      console.error("----", e);
    }
  };

  const onSubmit = async (values: any) => {
    try {
      let org_uuid = values.org_uuid;
      values.account_id = values?.account_id.account_id;
      values.parent_org = values?.parent_org.org_id;
      if (org_uuid) {
        delete values.org_uuid;
        await OrganizationService.editOrganization(values, org_uuid);
      } else {
        await OrganizationService.createOrganization(values);
      }
      setOpenOrganizationDialog(false);
      setNotification({
        message: `Organization ${org_uuid ? "updated" : "created"}successfully`,
        open: true,
      });
      getOrganizations();
      reset({});
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const handleDelete = async () => {
    try {
      await OrganizationService.deleteOrganization(selectedRow[0].org_uuid as string);
      setOpenDeleteConfirmation(false);
      setNotification({
        message: "Organization deleted successfully",
        open: true,
      });
      getOrganizations();
    } catch (e) {
      setNotification({
        message: "Error occurred",
        open: true,
        type: "error",
      });
      console.error("----", e);
    }
  };

  const onFilterChange = (model: GridFilterModel, details: GridCallbackDetails) => {
    console.log("filter data---", model, details);
    setRowFilter({ [model.items[0].columnField]: model.items[0].value });
  };

  const handleSearchChange = (searchText: string) => {
    setSearchText(searchText);
  };

  const handleClearSearch = () => {
    setSearchText("");
    setRowFilter({});
  };

  return (
    <>
      <Card sx={{ backgroundColor: "#2A2B31" }}>
        <CardHeader
          action={
            <Button
              sx={{ marginRight: "10px" }}
              variant="contained"
              onClick={() => {
                setOpenOrganizationDialog(true);
              }}
            >
              Create
            </Button>
          }
          title={t("common:organizations.title")}
        />
        <CardContent
          sx={{
            paddingBottom: "16px !important",
            "& .deleted-row": {
              backgroundColor: "#4c3939",
            },
          }}
        >
          <DataGrid
            autoHeight
            rows={tableRows}
            columns={columns}
            getRowId={(row) => row.org_id}
            rowsPerPageOptions={[rowsState.pageSize]}
            rowCount={totalRows}
            disableSelectionOnClick
            getRowClassName={(params) => (params.row.deleted ? "deleted-row" : "")}
            paginationMode="server"
            // sortingMode="server"
            // onSortModelChange={handleSortModelChange}
            filterMode="server"
            onFilterModelChange={onFilterChange}
            {...rowsState}
            onPageChange={(page) => setRowsState((prev: ITableRowsState) => ({ ...prev, page }))}
            components={{
              Toolbar: CustomTableToolbar,
            }}
            componentsProps={{
              panel: {
                anchorEl: filterButtonEl,
              },
              toolbar: {
                setFilterButtonEl,
                searchValue: searchText,
                onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  handleSearchChange(event.target.value);
                },
                showDeleted: showDeleted,
                onShowDeletedChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                  console.log(event.target.checked);
                  setShowDeleted(event.target.checked);
                },
                clearSearch: () => handleClearSearch(),
              },
            }}
          />
        </CardContent>
      </Card>
      <Dialog
        fullWidth={true}
        maxWidth="sm"
        open={openOrganizationDialog}
        onClose={() => setOpenOrganizationDialog(false)}
      >
        <DialogTitle>{`${getValues("org_id") ? "Edit" : "Create"} organization`}</DialogTitle>
        <Divider />
        <DialogContent>
          <Box component="main">
            <form>
              <Grid container spacing={2} mt={1} pr={2} pb={2} sx={{ overflow: "auto" }}>
                <Grid item xs={10}>
                  <FormInput
                    name="org_name"
                    type="text"
                    id="org_name"
                    label="Name"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={10}>
                  <FormAsyncAutocomplete
                    name="account_id"
                    id="account_id"
                    label="Account"
                    getOptions={(searchTerm: string) =>
                      AccountService.getAccounts({
                        count_per_page: 10,
                        page_no: 1,
                        filter: { account_name: searchTerm },
                        show_deleted: false,
                      })
                    }
                    labelKey="account_name"
                    valueKey="account_id"
                    rules={{
                      required: "This field is required",
                    }}
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={10}>
                  <FormAsyncAutocomplete
                    name="parent_org"
                    id="parent_org"
                    label="Parent Organization"
                    getOptions={(searchTerm: string) =>
                      OrganizationService.getOrganizations({
                        count_per_page: 10,
                        page_no: 1,
                        filter: { org_name: searchTerm },
                        show_deleted: false,
                      })
                    }
                    labelKey="org_name"
                    valueKey="org_id"
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button sx={{ mr: 2 }} onClick={() => setOpenOrganizationDialog(false)}>
            Cancel
          </Button>
          <Button sx={{ mr: 2 }} variant="contained" onClick={handleSubmit(onSubmit)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        onProceed={handleDelete}
        title="Delete"
        content="If you are sure you want to delete the selected organization?"
        confirmationText={selectedRow[0]?.org_name}
      />
    </>
  );
};

export default Organization;
